import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, CardActionArea, CardContent, Divider, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import {CallStatusDropdown, StageDropdown} from './dropdowns';

import backend from 'utils/backend';
import moment from 'moment';

const Card = (props) => {
  const item = props.item;
  const onItemUpdate = props.onItemUpdate;
  const progressWrap = props.statusProgressWrap;

  const [cardItem, setCardItem] = React.useState(item);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = React.useState(false);
  const [isCallStatusDropdownOpen, setIsCallStatusDropdownOpen] = React.useState(false);

  React.useEffect(() => {
    setCardItem(item);
  }, [item]);

  const clientCareplan = cardItem.clientCarePlan;
  const clientStatusLog = cardItem.clientLastLogStatusUpdate;
  const clientLastMessage = cardItem.clientLastMessage;
  const clientRn = cardItem.clientRn;
  const clientName = cardItem.clientName;
  const clientCreatedAt = cardItem.createdAt;
  const status = cardItem.status;
  const callStatus = cardItem.clientCallStatus?.introCallStatus;
  const isAnyOverlayOpen = isStatusDropdownOpen || isCallStatusDropdownOpen;

  const clientRelationships = React.useMemo(() => {
    const relationships = Object.values(cardItem.relationships ?? {});
    if (!relationships.length) return [];

    return relationships.slice().sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    });
  }, [cardItem.relationships]);

  const primaryRelationship = clientRelationships[0];
  const notSetLabel = translate('clientLead.ClientLeadList.Card2.SubtitleValue.NotSet');
  const clientLocation = clientCareplan?.generalInformation?.address || notSetLabel;

  const clientDaysInStage = React.useMemo(() => {
    if (!clientStatusLog) {
      return Math.floor(moment().diff(moment(clientCreatedAt), 'days', true));
    } else {
      return Math.floor(moment().diff(moment(clientStatusLog.createdAt), 'days', true));
    }
  }, [clientStatusLog, clientCreatedAt]);

  const clientDaysInStageText = React.useMemo(() => {
    return translate(
        `clientLead.ClientLeadList.Card.SubtitleValue.${clientDaysInStage === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysInStage},
    );
  }, [clientDaysInStage]);

  const clientDaysSinceLastContact = React.useMemo(() => {
    if (!clientLastMessage) return null;
    return Math.floor(moment().diff(moment(clientLastMessage.createdAt), 'days', true));
  }, [clientLastMessage]);

  const clientDaysSinceLastContactText = React.useMemo(() => {
    if (clientDaysSinceLastContact === null) return null;
    return translate(
        `clientLead.ClientLeadList.Card.SubtitleValue.${clientDaysSinceLastContact === 1 ? 'Day' : 'Days'}`,
        {number: clientDaysSinceLastContact},
    );
  }, [clientDaysSinceLastContact]);

  const handleSetStatus = async (nextStatus, message = null) => {
    if (!nextStatus || nextStatus === status) {
      return;
    }

    const previousItem = cardItem;
    const optimisticItem = {
      ...cardItem,
      status: nextStatus,
      clientLastLogStatusUpdate: {
        ...cardItem.clientLastLogStatusUpdate,
        createdAt: new Date().toISOString(),
      },
    };

    setCardItem(optimisticItem);
    onItemUpdate?.(clientRn, optimisticItem);

    try {
      await progressWrap(async () => {
        const body = {
          status: nextStatus,
        };

        if (message) {
          body.message = message;
        }

        const {clientDetail, updateLog} = await backend.client.updateStatus(clientRn, body);
        const nextItem = {
          ...optimisticItem,
          ...clientDetail,
          clientLastLogStatusUpdate: updateLog ?? clientDetail.clientLastLogStatusUpdate,
        };

        setCardItem(nextItem);
        onItemUpdate?.(clientRn, nextItem);
      });
    } catch (error) {
      setCardItem(previousItem);
      onItemUpdate?.(clientRn, previousItem);
      console.error(error);
    }
  };

  const handleSetCallStatus = async (nextCallStatus) => {
    if (!nextCallStatus || nextCallStatus === callStatus) {
      return;
    }

    const previousItem = cardItem;
    const optimisticItem = {
      ...cardItem,
      clientCallStatus: {
        ...(cardItem.clientCallStatus ?? {}),
        introCallStatus: nextCallStatus,
      },
    };

    setCardItem(optimisticItem);
    onItemUpdate?.(clientRn, optimisticItem);

    try {
      await progressWrap(async () => {
        const {callStatus: nextCallStatusData} = await backend.client.editCallStatus(clientRn, {
          introCallStatus: nextCallStatus,
        });
        const nextItem = {
          ...optimisticItem,
          clientCallStatus: nextCallStatusData,
        };

        setCardItem(nextItem);
        onItemUpdate?.(clientRn, nextItem);
      });
    } catch (error) {
      setCardItem(previousItem);
      onItemUpdate?.(clientRn, previousItem);
      console.error(error);
    }
  };

  return (
    <React.Fragment>
      <CardActionArea
        onClick={(event) => {
          if (!isAnyOverlayOpen) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
        }}
        sx={{
          height: '100%',
          textAlign: 'left',
        }}
        to={`/client/${clientRn}`}
      >
        <CardContent
          sx={{
            p: '20px !important',
            height: '100%',
          }}
        >
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Typography
                    component='div'
                    sx={{
                      color: '#1A1916',
                      fontSize: '16px',
                      fontWeight: 600,
                      lineHeight: '21px',
                    }}
                  >
                    {clientName}
                  </Typography>
                  <Typography
                    component='div'
                    sx={{
                      color: '#1A1916',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '16px',
                      mt: '4px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {primaryRelationship?.name || notSetLabel}
                  </Typography>
                  {
                    primaryRelationship?.connectionType &&
                    <Typography
                      component='div'
                      sx={{
                        color: '#A8A49E',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        wordBreak: 'break-word',
                      }}
                    >
                      ({primaryRelationship.connectionType})
                    </Typography>
                  }
                </Box>
                <Stack
                  spacing={0.75}
                  sx={{
                    alignItems: 'flex-end',
                    flexShrink: 0,
                  }}
                >
                  <StageDropdown
                    onOverlayChange={setIsStatusDropdownOpen}
                    selectedStage={status}
                    setSelectedStage={handleSetStatus}
                  />
                  {
                    callStatus &&
                    <CallStatusDropdown
                      onOverlayChange={setIsCallStatusDropdownOpen}
                      selectedStatus={callStatus}
                      setSelectedStatus={handleSetCallStatus}
                    />
                  }
                </Stack>
              </Box>

              <Divider sx={{borderColor: '#EBEBEB'}} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  columnGap: 2,
                  rowGap: 2,
                }}
              >
                <Box>
                  <Typography
                    component='div'
                    sx={{
                      color: '#A8A49E',
                      fontSize: '11px',
                      fontWeight: 600,
                      lineHeight: '14px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Translate expression='clientLead.ClientLeadList.Card2.Subtitles.DaysInStage' />
                  </Typography>
                  <Typography
                    component='div'
                    sx={{
                      color: '#1A1916',
                      fontSize: '11px',
                      fontWeight: 400,
                      lineHeight: '14px',
                      mt: '4px',
                    }}
                  >
                    <Translate
                      expression='clientLead.ClientLeadList.Card.Subtitles.InStage'
                      substitute={{value: clientDaysInStageText}}
                    />
                  </Typography>
                  {
                    clientLastMessage &&
                    <Typography
                      component='div'
                      sx={{
                        color: '#1A1916',
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '14px',
                        mt: '4px',
                      }}
                    >
                      <Translate
                        expression='clientLead.ClientLeadList.Card.Subtitles.SinceLastMsg'
                        substitute={{value: clientDaysSinceLastContactText}}
                      />
                    </Typography>
                  }
                </Box>

                <Box
                  sx={{
                    ml: {
                      xs: 0, md: 4, lg: 8,
                    },
                  }}
                >
                  <Typography
                    component='div'
                    sx={{
                      color: '#A8A49E',
                      fontSize: '11px',
                      fontWeight: 600,
                      lineHeight: '14px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Translate expression='clientLead.ClientLeadList.Card2.Subtitles.Location' />
                  </Typography>
                  <Typography
                    component='div'
                    sx={{
                      color: clientLocation === notSetLabel ? '#A8A49E' : '#1A1916',
                      fontSize: '11px',
                      fontWeight: 400,
                      lineHeight: '14px',
                      mt: '4px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {clientLocation}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    component='div'
                    sx={{
                      color: '#A8A49E',
                      fontSize: '11px',
                      fontWeight: 600,
                      lineHeight: '14px',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Translate expression='clientLead.ClientLeadList.Card2.Subtitles.Created' />
                  </Typography>
                  <Typography
                    component='div'
                    sx={{
                      color: '#1A1916',
                      fontSize: '11px',
                      fontWeight: 400,
                      lineHeight: '14px',
                      mt: '4px',
                    }}
                  >
                    {clientCreatedAt ? moment(clientCreatedAt).format('MMM D, YYYY') : '-'}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </React.Fragment>
  );
};

Card.propTypes = {
  item: PropTypes.object.isRequired,
  onItemUpdate: PropTypes.func,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(Card));
